import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, getDocs, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../shared/storage/firebase';
import GoogleMapsService from '../../../shared/services/GoogleMapsService';
import type { Squad, User } from '../../../types';
import type { PartnerLocation } from '../../partners/types';
import {
  canonicalSquadMapRecords,
  groupSquadRecordsByMapPoint,
  groupTeachersByMapPoint,
  storedTeacherMapCoordinates,
  squadNeedsStaff,
  squadScheduleLabel,
  teacherMapAddressFingerprint,
  teacherMapSearchQueries,
  teacherMapState,
  teacherMatchesMapFilters,
  type CanonicalSquadMapRecord,
  type SquadMapFilters,
  type TeacherMapFilters,
} from '../utils/adminMap';
import { coordinatesFromPlaceId, coordinatesFromPlaceQuery } from '../../../shared/utils/placesCoordinates';
import {
  displayCurriculum,
  displaySeason,
  isCurrentOrUpcomingListing,
  listingFromSquad,
} from '../../squads/utils/staffSquadListing';
import '../styles/AdminTeacherMap.css';

type Coordinates = { lat: number; lng: number };
type GeocodeState = { status: 'pending' | 'located' | 'unlocated'; coordinates?: Coordinates; reason?: string };

const DEFAULT_CENTER = { lat: 39.5, lng: -98.35 };
const TEACHER_COLOR = '#2563eb';
const SQUAD_COLOR = '#7c3aed';
const SQUAD_NEEDED_COLOR = '#f59e0b';

async function resolveTeacherMapPoint(teacher: User, map?: any): Promise<Coordinates> {
  const queries = teacherMapSearchQueries(teacher.shippingAddress);
  for (const query of queries) {
    const coordinates = await coordinatesFromPlaceQuery(query, map);
    if (coordinates) return coordinates;
  }
  const placeId = String(teacher.shippingAddress?.placeId || '').trim();
  if (placeId) {
    const coordinates = await coordinatesFromPlaceId(placeId, map);
    if (coordinates) return coordinates;
  }
  throw new Error(queries.length ? 'Places ZIP/city lookup returned no result' : 'Shipping address missing');
}

function teacherInfoNode(teacher: User): HTMLElement {
  const root = document.createElement('div');
  root.className = 'admin-map-card admin-map-card--teacher';
  if (teacher.headshotUrl) {
    const photo = document.createElement('img');
    photo.className = 'admin-map-card__photo';
    photo.src = teacher.headshotUrl;
    photo.alt = '';
    root.appendChild(photo);
  }
  const copy = document.createElement('div');
  const name = document.createElement('strong');
  name.className = 'admin-map-card__title';
  name.textContent = teacher.displayName || 'Unnamed teacher';
  copy.appendChild(name);
  const city = String(teacher.shippingAddress?.city || '').trim();
  const state = teacherMapState(teacher);
  const place = [city, state].filter(Boolean).join(', ');
  if (place) {
    const location = document.createElement('div');
    location.className = 'admin-map-card__muted';
    location.textContent = place;
    copy.appendChild(location);
  }
  root.appendChild(copy);
  return root;
}

function teacherClusterNode(teachers: User[]): HTMLElement {
  if (teachers.length === 1) return teacherInfoNode(teachers[0]);
  const root = document.createElement('div');
  root.className = 'admin-map-popup';
  const heading = document.createElement('h3');
  heading.textContent = `${teachers.length} teachers near this ZIP`;
  root.appendChild(heading);
  const list = document.createElement('div');
  list.className = 'admin-map-popup__list';
  teachers.forEach((teacher) => list.appendChild(teacherInfoNode(teacher)));
  root.appendChild(list);
  root.addEventListener('wheel', (event) => event.stopPropagation(), { passive: true });
  return root;
}

function squadLabel(squad: Squad): string {
  const listing = listingFromSquad(squad);
  return `${displayCurriculum(listing)} · ${displaySeason(listing)}`;
}

function squadClusterNode(
  records: CanonicalSquadMapRecord[],
  onOpenSquad: (squadId: string) => void,
): HTMLElement {
  const root = document.createElement('div');
  root.className = 'admin-map-popup';
  const heading = document.createElement('h3');
  heading.textContent = records[0]?.location?.name || 'Squad location';
  root.appendChild(heading);
  if (records.length > 1) {
    const hint = document.createElement('p');
    hint.className = 'admin-map-popup__hint';
    hint.textContent = `${records.length} squads · scroll to see all`;
    root.appendChild(hint);
  }
  const list = document.createElement('div');
  list.className = 'admin-map-popup__list';
  records.forEach((record) => {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'admin-map-popup__item';
    const title = document.createElement('strong');
    title.textContent = record.squad.title || 'Untitled squad';
    const meta = document.createElement('p');
    meta.textContent = squadLabel(record.squad);
    const schedule = document.createElement('p');
    schedule.textContent = squadScheduleLabel(record.squad);
    const staff = document.createElement('p');
    const needed = squadNeedsStaff(record.squad);
    staff.textContent = needed ? 'Lead needed' : 'Lead assigned';
    if (needed) staff.className = 'admin-map-popup__needed';
    item.append(title, meta, schedule, staff);
    item.addEventListener('click', () => onOpenSquad(record.squad.id));
    list.appendChild(item);
  });
  root.appendChild(list);
  root.addEventListener('wheel', (event) => event.stopPropagation(), { passive: true });
  return root;
}

const AdminTeacherMap: React.FC = () => {
  const navigate = useNavigate();
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const infoWindowRef = useRef<any>(null);
  const geocodeCacheRef = useRef<Map<string, Coordinates | null>>(new Map());
  const [mapsReady, setMapsReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [teachers, setTeachers] = useState<User[]>([]);
  const [squads, setSquads] = useState<Squad[]>([]);
  const [locations, setLocations] = useState<PartnerLocation[]>([]);
  const [teacherGeocodes, setTeacherGeocodes] = useState<Record<string, GeocodeState>>({});
  const [locationGeocodes, setLocationGeocodes] = useState<Record<string, Coordinates>>({});
  const [teacherFilters, setTeacherFilters] = useState<TeacherMapFilters>({
    staffStatus: 'active',
    state: '',
    subject: '',
  });
  const [squadFilters, setSquadFilters] = useState<SquadMapFilters>({
    curriculum: '',
    season: '',
    staffingNeed: 'all',
  });
  const [showTeachers, setShowTeachers] = useState(true);
  const [showSquads, setShowSquads] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        if (!GoogleMapsService.isApiLoaded()) {
          if (!apiKey) throw new Error('Google Maps API key is not configured.');
          await GoogleMapsService.load(apiKey);
        }
        if (!cancelled) setMapsReady(true);
      } catch (nextError) {
        if (!cancelled) setError(nextError instanceof Error ? nextError.message : 'Could not load Google Maps.');
      }
    };
    void load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [teacherSnapshot, squadSnapshot, locationSnapshot] = await Promise.all([
          getDocs(query(collection(db, 'users'), where('role', '==', 'teacher'))),
          getDocs(collection(db, 'squads')),
          getDocs(collection(db, 'locations')),
        ]);
        if (cancelled) return;
        setTeachers(teacherSnapshot.docs.map((item) => ({ id: item.id, ...item.data() } as User)));
        setSquads(squadSnapshot.docs.map((item) => ({ id: item.id, ...item.data() } as Squad)));
        setLocations(locationSnapshot.docs.map((item) => ({ id: item.id, ...item.data() } as PartnerLocation)));
      } catch (nextError) {
        if (!cancelled) setError(nextError instanceof Error ? nextError.message : 'Could not load map records.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!mapsReady || !mapElementRef.current || mapRef.current) return;
    const googleMaps = (window as any).google.maps;
    mapRef.current = new googleMaps.Map(mapElementRef.current, {
      center: DEFAULT_CENTER,
      zoom: 4,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
    });
    infoWindowRef.current = new googleMaps.InfoWindow();
  }, [mapsReady]);

  useEffect(() => {
    if (!mapsReady || teachers.length === 0) return;
    let cancelled = false;

    const geocodeTeachers = async () => {
      const initial: Record<string, GeocodeState> = {};
      teachers.forEach((teacher) => {
        const stored = storedTeacherMapCoordinates(teacher);
        initial[teacher.id] = stored
          ? { status: 'located', coordinates: stored }
          : { status: 'pending' };
      });
      setTeacherGeocodes(initial);

      for (const teacher of teachers) {
        if (cancelled) return;
        const stored = storedTeacherMapCoordinates(teacher);
        if (stored) continue;
        const queries = teacherMapSearchQueries(teacher.shippingAddress);
        const fingerprint = teacherMapAddressFingerprint(teacher.shippingAddress)
          || queries[0]
          || '';
        if (!queries.length && !String(teacher.shippingAddress?.placeId || '').trim()) {
          setTeacherGeocodes((current) => ({
            ...current,
            [teacher.id]: { status: 'unlocated', reason: 'Shipping address missing' },
          }));
          continue;
        }
        let coordinates = fingerprint ? geocodeCacheRef.current.get(fingerprint) : undefined;
        let failureReason = '';
        if (coordinates === undefined) {
          try {
            coordinates = await resolveTeacherMapPoint(teacher, mapRef.current);
            if (fingerprint) geocodeCacheRef.current.set(fingerprint, coordinates);
            try {
              await updateDoc(doc(db, 'users', teacher.id), {
                teacherMapLocation: {
                  latitude: coordinates.lat,
                  longitude: coordinates.lng,
                  addressFingerprint: fingerprint,
                  placeId: String(teacher.shippingAddress?.placeId || '').trim() || null,
                  geocodedAt: serverTimestamp(),
                },
              });
            } catch (persistError) {
              console.warn(`Located ${teacher.displayName || teacher.id}, but could not cache coordinates.`, persistError);
            }
          } catch (geocodeError) {
            failureReason = String((geocodeError as Error)?.message || 'UNKNOWN_ERROR');
            coordinates = null;
            if (fingerprint) geocodeCacheRef.current.set(fingerprint, null);
          }
        }
        if (cancelled) return;
        setTeacherGeocodes((current) => ({
          ...current,
          [teacher.id]: coordinates
            ? { status: 'located', coordinates }
            : {
              status: 'unlocated',
              reason: failureReason
                ? `Map lookup: ${failureReason}`
                : 'ZIP or city could not be located',
            },
        }));
      }
    };

    void geocodeTeachers();
    return () => { cancelled = true; };
  }, [mapsReady, teachers]);

  useEffect(() => {
    if (!mapsReady || locations.length === 0) return;
    let cancelled = false;
    const geocodeMissingLocations = async () => {
      const updates: Record<string, Coordinates> = {};
      for (const location of locations) {
        if (cancelled) return;
        if (
          typeof location.latitude === 'number'
          && Number.isFinite(location.latitude)
          && typeof location.longitude === 'number'
          && Number.isFinite(location.longitude)
        ) continue;
        const address = String(location.address || '').trim();
        if (!address || locationGeocodes[location.id]) continue;
        let coordinates = geocodeCacheRef.current.get(address);
        if (coordinates === undefined) {
          try {
            coordinates = await coordinatesFromPlaceQuery(address, mapRef.current);
            geocodeCacheRef.current.set(address, coordinates);
          } catch (_) {
            coordinates = null;
            geocodeCacheRef.current.set(address, null);
          }
        }
        if (coordinates) updates[location.id] = coordinates;
      }
      if (!cancelled && Object.keys(updates).length) {
        setLocationGeocodes((current) => ({ ...current, ...updates }));
      }
    };
    void geocodeMissingLocations();
    return () => { cancelled = true; };
  }, [locationGeocodes, locations, mapsReady]);

  const stateOptions = useMemo(() => Array.from(new Set(
    teachers.map(teacherMapState).filter(Boolean),
  )).sort(), [teachers]);
  const subjectOptions = useMemo(() => Array.from(new Set(
    teachers.flatMap((teacher) => teacher.subjects || []).map((subject) => String(subject).trim()).filter(Boolean),
  )).sort(), [teachers]);
  const currentSquads = useMemo(
    () => squads.filter((squad) => isCurrentOrUpcomingListing(listingFromSquad(squad))),
    [squads],
  );
  const curriculumOptions = useMemo(() => Array.from(new Set(
    currentSquads.map((squad) => displayCurriculum(listingFromSquad(squad))).filter(Boolean),
  )).sort(), [currentSquads]);
  const seasonOptions = useMemo(() => Array.from(new Set(
    currentSquads.map((squad) => displaySeason(listingFromSquad(squad))).filter((season) => season !== 'Season not set'),
  )).sort(), [currentSquads]);

  const filteredTeachers = useMemo(
    () => teachers.filter((teacher) => teacherMatchesMapFilters(teacher, teacherFilters)),
    [teacherFilters, teachers],
  );
  const allSquadRecords = useMemo(() => canonicalSquadMapRecords(currentSquads, locations), [currentSquads, locations]);
  const filteredSquadRecords = useMemo(
    () => allSquadRecords.filter(({ squad }) => {
      const listing = listingFromSquad(squad);
      if (squadFilters.curriculum && displayCurriculum(listing) !== squadFilters.curriculum) return false;
      if (squadFilters.season && displaySeason(listing) !== squadFilters.season) return false;
      if (squadFilters.staffingNeed === 'needs_staff' && !squadNeedsStaff(squad)) return false;
      if (squadFilters.staffingNeed === 'staffed' && squadNeedsStaff(squad)) return false;
      return true;
    }),
    [allSquadRecords, squadFilters],
  );

  const unlocatedTeachers = filteredTeachers.filter((teacher) => teacherGeocodes[teacher.id]?.status === 'unlocated');
  const pendingTeacherCount = filteredTeachers.filter((teacher) => teacherGeocodes[teacher.id]?.status === 'pending').length;
  const unlocatedSquads = filteredSquadRecords.filter((record) => (
    (record.latitude == null || record.longitude == null)
    && !locationGeocodes[record.location?.id || '']
  ));

  useEffect(() => {
    const map = mapRef.current;
    if (!mapsReady || !map) return;
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];
    const googleMaps = (window as any).google.maps;
    if (!infoWindowRef.current) infoWindowRef.current = new googleMaps.InfoWindow({ maxWidth: 340 });
    const infoWindow = infoWindowRef.current;
    infoWindow.close();
    const bounds = new googleMaps.LatLngBounds();
    let pointCount = 0;

    const addMarker = (
      position: Coordinates,
      options: { title: string; label?: string; color: string; content: () => HTMLElement },
    ) => {
      const labelText = options.label?.trim() || '';
      const marker = new googleMaps.Marker({
        map,
        position,
        title: options.title,
        ...(labelText ? {
          label: {
            text: labelText.length > 28 ? `${labelText.slice(0, 27)}…` : labelText,
            color: '#25332a',
            fontSize: '11px',
            fontWeight: '700',
            className: 'admin-map-marker-label',
          },
        } : {}),
        icon: {
          path: googleMaps.SymbolPath.CIRCLE,
          fillColor: options.color,
          fillOpacity: 0.95,
          strokeColor: '#ffffff',
          strokeWeight: 3,
          scale: 10,
          labelOrigin: new googleMaps.Point(0, -2.1),
        },
      });
      const open = () => {
        infoWindow.setContent(options.content());
        infoWindow.open({ map, anchor: marker });
      };
      marker.addListener('mouseover', open);
      marker.addListener('click', open);
      markersRef.current.push(marker);
      bounds.extend(position);
      pointCount += 1;
    };

    if (showTeachers) {
      groupTeachersByMapPoint(filteredTeachers.flatMap((teacher) => {
        const coordinates = teacherGeocodes[teacher.id]?.coordinates;
        return coordinates ? [{ teacher, lat: coordinates.lat, lng: coordinates.lng }] : [];
      })).forEach((cluster) => {
        const isGroup = cluster.teachers.length > 1;
        addMarker({ lat: cluster.lat, lng: cluster.lng }, {
          title: isGroup ? `${cluster.teachers.length} teachers` : (cluster.teachers[0].displayName || 'Teacher'),
          label: isGroup ? `${cluster.teachers.length} teachers` : undefined,
          color: TEACHER_COLOR,
          content: () => teacherClusterNode(cluster.teachers),
        });
      });
    }

    if (showSquads) {
      groupSquadRecordsByMapPoint(filteredSquadRecords, locationGeocodes).forEach((cluster) => {
        const isGroup = cluster.records.length > 1;
        const first = cluster.records[0].squad;
        const needsStaff = cluster.records.some((record) => squadNeedsStaff(record.squad));
        addMarker({ lat: cluster.lat, lng: cluster.lng }, {
          title: isGroup ? `${cluster.records.length} squads at this location` : (first.title || 'Squad'),
          label: isGroup ? `${cluster.records.length} squads here` : squadLabel(first),
          color: needsStaff ? SQUAD_NEEDED_COLOR : SQUAD_COLOR,
          content: () => squadClusterNode(cluster.records, (squadId) => navigate(`/app/admin/squads/${squadId}`)),
        });
      });
    }

    const mapClick = map.addListener('click', () => infoWindow.close());
    if (pointCount > 0) {
      map.fitBounds(bounds, 56);
      if (pointCount === 1) {
        googleMaps.event.addListenerOnce(map, 'idle', () => {
          if (map.getZoom() > 13) map.setZoom(13);
        });
      }
    } else {
      map.setCenter(DEFAULT_CENTER);
      map.setZoom(4);
    }

    return () => {
      mapClick.remove();
      infoWindow.close();
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];
    };
  }, [filteredSquadRecords, filteredTeachers, locationGeocodes, mapsReady, navigate, showSquads, showTeachers, teacherGeocodes]);

  const locatedTeacherCount = filteredTeachers.filter((teacher) => teacherGeocodes[teacher.id]?.status === 'located').length;
  const locatedSquadCount = filteredSquadRecords.length - unlocatedSquads.length;
  const unlocatedCount = unlocatedTeachers.length + unlocatedSquads.length;

  return (
    <div className="admin-teacher-map">
      <header className="admin-teacher-map__header">
        <button type="button" className="admin-teacher-map__back" onClick={() => navigate('/app/admin', {
          state: { adminTab: 'users', adminUserTab: 'teachers' },
        })}>
          ← Teachers
        </button>
        <div>
          <h1>Teacher & Squad Map</h1>
          <p>Programs at the same campus share one pin. Hover or click to see every squad. Teachers are ZIP/city dots.</p>
        </div>
        <div className="admin-teacher-map__legend" aria-label="Map layers">
          <label>
            <input type="checkbox" checked={showTeachers} onChange={(event) => setShowTeachers(event.target.checked)} />
            <i className="is-teacher" /> Teachers
          </label>
          <label>
            <input type="checkbox" checked={showSquads} onChange={(event) => setShowSquads(event.target.checked)} />
            <i className="is-squad" /> Squads
          </label>
          <span><i className="is-needed" /> Lead needed</span>
        </div>
      </header>

      <section className="admin-teacher-map__filters" aria-label="Map filters">
        <div className="admin-teacher-map__filter-group">
          <strong>Teachers</strong>
          <label>Status
            <select value={teacherFilters.staffStatus} onChange={(event) => setTeacherFilters((current) => ({
              ...current,
              staffStatus: event.target.value as TeacherMapFilters['staffStatus'],
            }))}>
              <option value="active">Active</option>
              <option value="applicant">Applicant</option>
              <option value="offboarded">Offboarded</option>
              <option value="all">All</option>
            </select>
          </label>
          <label>State
            <select value={teacherFilters.state} onChange={(event) => setTeacherFilters((current) => ({ ...current, state: event.target.value }))}>
              <option value="">All</option>
              {stateOptions.map((state) => <option key={state} value={state}>{state}</option>)}
            </select>
          </label>
          <label>Subject
            <select value={teacherFilters.subject} onChange={(event) => setTeacherFilters((current) => ({ ...current, subject: event.target.value }))}>
              <option value="">All</option>
              {subjectOptions.map((subject) => <option key={subject} value={subject}>{subject}</option>)}
            </select>
          </label>
        </div>
        <div className="admin-teacher-map__filter-group">
          <strong>Squads</strong>
          <label>Curriculum
            <select value={squadFilters.curriculum} onChange={(event) => setSquadFilters((current) => ({ ...current, curriculum: event.target.value }))}>
              <option value="">All</option>
              {curriculumOptions.map((curriculum) => <option key={curriculum} value={curriculum}>{curriculum}</option>)}
            </select>
          </label>
          <label>Season
            <select value={squadFilters.season} onChange={(event) => setSquadFilters((current) => ({ ...current, season: event.target.value }))}>
              <option value="">All</option>
              {seasonOptions.map((season) => <option key={season} value={season}>{season}</option>)}
            </select>
          </label>
          <label>Staffing
            <select value={squadFilters.staffingNeed} onChange={(event) => setSquadFilters((current) => ({
              ...current,
              staffingNeed: event.target.value as SquadMapFilters['staffingNeed'],
            }))}>
              <option value="all">All</option>
              <option value="needs_staff">Needs lead</option>
              <option value="staffed">Lead assigned</option>
            </select>
          </label>
        </div>
      </section>

      {error && <div className="admin-teacher-map__error" role="alert">{error}</div>}
      <main className="admin-teacher-map__content">
        {loading && <div className="admin-teacher-map__loading">Loading map…</div>}
        <div className="admin-teacher-map__canvas" ref={mapElementRef} aria-label="Teacher and squad locations" />
      </main>
      <footer className="admin-teacher-map__summary">
        {showSquads && <span><strong>{locatedSquadCount}</strong> squads</span>}
        {showTeachers && <span><strong>{locatedTeacherCount}</strong> teachers</span>}
        {pendingTeacherCount > 0 && <span>Locating {pendingTeacherCount} teacher{pendingTeacherCount === 1 ? '' : 's'}…</span>}
        {unlocatedCount > 0 && (
          <details>
            <summary>{unlocatedCount} without map coordinates</summary>
            {unlocatedTeachers.length > 0 && (
              <ul>
                {unlocatedTeachers.map((teacher) => (
                  <li key={teacher.id}>
                    {teacher.displayName || 'Unnamed teacher'}
                    {teacherGeocodes[teacher.id]?.reason ? ` — ${teacherGeocodes[teacher.id]?.reason}` : ''}
                  </li>
                ))}
              </ul>
            )}
            {unlocatedSquads.length > 0 && (
              <ul>
                {unlocatedSquads.map(({ squad, location }) => (
                  <li key={squad.id}>
                    {squad.title || 'Untitled squad'} — {location?.name || 'Location missing'}
                  </li>
                ))}
              </ul>
            )}
          </details>
        )}
      </footer>
    </div>
  );
};

export default AdminTeacherMap;
